import { useState, useEffect } from "react";
import {
  DatePicker,
  notification,
  message,
  Input,
  Select,
  Form,
  Modal,
  Row,
  Col,
  Space,
  Button
} from "antd";
import { useEmailReportRequestMutation } from "../../redux/services/reportApi";
import { useGetLocationsQuery } from "../../redux/services/locationsApi";
import { useGetBrandsQuery } from "../../redux/services/brandApi";
import { useAppSelector } from "../../redux/hooks";
import { useLazyGetBrandModelsQuery } from "../../redux/services/brandModelApi";
import {
  reportTypeOptions,
  clientReportOptions,
  financeReportOptions,
  adminReportTypeOptions
} from "../../constants/helperConstants";
import ErrorDetails from "../../components/ErrorDetails";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ReportDownloadModal = (props: any) => {
  const { showModal, setShowModal } = props;
  const [form] = Form.useForm();
  const auth = useAppSelector<any>((store) => store.auth);
  const user = auth.user;
  const [selectedRange, setSelectedRange] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [brandId, setBrandId] = useState<string | null>(null);

  const [notifier, contextHolder] = notification.useNotification();

  const isEditUser = user?.roles?.some((role: { role_type: string }) =>
    [
      "super_user",
      "mis",
      "city_manager",
      "zonal_manager",
      "management",
      "finance_controller"
    ].includes(role.role_type)
  );

  const isAdminUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user", "mis"].includes(role.role_type)
  );

  const isFinanceController = user?.roles?.some((role: { role_type: string }) =>
    ["finance_controller"].includes(role.role_type)
  );

  const { data: brandsData } = useGetBrandsQuery({ all_pages: true });
  const brands = brandsData?.brands || [];

  const { data: zoneLocationsData } = useGetLocationsQuery({
    location_type: "zone",
    all_pages: true
  });
  const zones = zoneLocationsData?.locations;

  const zoneOptions = zones?.map((zone: any) => ({ value: zone.id, label: zone.name })) || [];

  const filteredReportTypeOptions = isAdminUser
    ? [...reportTypeOptions, ...financeReportOptions, ...adminReportTypeOptions]
    : reportTypeOptions;
  const isClient = user?.user_type === "client";

  const [emailReportRequest] = useEmailReportRequestMutation();

  const [triggerGetBrandModels, { data: brandModelsData }] = useLazyGetBrandModelsQuery();
  const brandModels = brandModelsData?.brand_models || [];

  const [brandModelCodeOptions, setBrandModelCodeOptions] = useState([]);

  const handleBrandSelect = (selectedBrandId: string | null) => {
    form.setFieldValue("brand_model_id", undefined);
    setBrandId(selectedBrandId);
  };

  useEffect(() => {
    if (brandId?.length) {
      triggerGetBrandModels({ all_pages: true, brand_id: brandId });
    } else {
      setBrandModelCodeOptions([]);
    }
  }, [brandId]);

  useEffect(() => {
    if (brandModels.length) {
      setBrandModelCodeOptions(
        brandModels.flat().map((model: any) => ({ value: model.id, label: model.code }))
      );
    }
  }, [brandModels]);

  const onEmailReportRequest = async (values: any) => {
    const response = await emailReportRequest({ ...values }).unwrap();
    if (response.success) {
      message.success(
        `Email report request submitted successfully and ${values.report_type} report will be send to your email`
      );
      form.resetFields();
    } else {
      message.error("Some thing went wrong...");
    }
    setShowModal(false);
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    // code for on finish
    if (values.date_range && values.date_range[0]) {
      values["start_date"] = values.date_range[0]?.format("YYYY-MM-DD");
    }
    if (values.date_range && values.date_range[1]) {
      values["end_date"] = values.date_range[1]?.format("YYYY-MM-DD");
    } else {
      values["end_date"] = values.date_range[0]?.format("YYYY-MM-DD");
    }
    try {
      const response = await Promise.resolve(onEmailReportRequest(values));
      console.log("Success:", response);
    } catch (error: any) {
      console.error("Error:", error);
      notifier.info({
        message: "Report download operation failed, please retry in sometime",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    // code for finish failed
    console.log(errorInfo);
  };

  // Function to handle RangePicker change
  const handleRangeChange = (dates: any, dateStrings: string[]) => {
    setSelectedRange(dates);
  };

  function disabledDate(current: any) {
    if (selectedRange && selectedRange.length === 0) {
      return false; // No range selected yet, all dates are enabled
    }

    if (selectedRange && selectedRange[0]) {
      const daysAfterStart = selectedRange[0].clone().add(92, "days");
      if (selectedRange[1] && selectedRange[1] > daysAfterStart) {
        const range = selectedRange;
        range[1] = null;
        setSelectedRange(range);
      }
      return current.isAfter(daysAfterStart);
    }

    return false;
  }
  return (
    <Modal
      title="Email Report Request"
      open={showModal}
      width={1000}
      onCancel={() => setShowModal(false)}
      onOk={() => setShowModal(false)}
      style={{ width: "800px", height: "600px", maxWidth: "90%" }} // Custom size
      footer={[
        <Button key="cancel" onClick={() => setShowModal(false)}>
          Cancel
        </Button>
      ]}
    >
      <Form
        name="report_download_form"
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{ date_range: [null, null] }}
        autoComplete="off"
      >
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col className="gutter-row" span={12}>
            <Form.Item
              label=""
              name="date_range"
              rules={[{ required: true, message: "Please select from date" }]}
            >
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  disabled
                  value={"Date Range"}
                  style={{ width: "200px", color: "rgba(0, 0, 0, 0.65)", cursor: "auto" }}
                />
                <Form.Item name="date_range" noStyle>
                  <RangePicker
                    disabledDate={disabledDate}
                    onCalendarChange={handleRangeChange}
                    format="DD-MM-YYYY"
                    value={[selectedRange && selectedRange[0], selectedRange && selectedRange[1]]}
                    disabled={[false, !selectedRange || (!selectedRange && !selectedRange[0])]}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
          </Col>
          {(!isClient || isEditUser) && (
            <Col className="gutter-row" span={12}>
              <Form.Item>
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    disabled
                    value={"Brand"}
                    style={{ width: "200px", color: "rgba(0, 0, 0, 0.65)", cursor: "auto" }}
                  />
                  <Form.Item name="brand_id" noStyle>
                    <Select
                      placeholder="Select brand"
                      allowClear
                      showSearch
                      mode="multiple"
                      optionFilterProp="children"
                      onChange={handleBrandSelect}
                      filterOption={(input, option: any) =>
                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {brands?.map((brand: any) => (
                        <Option key={brand?.name} value={brand?.id}>
                          {brand?.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
          )}
          <Col className="gutter-row" span={12}>
            <Form.Item>
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  disabled
                  value={"Brand Model Code"}
                  style={{
                    width: "200px",
                    color: "rgba(0, 0, 0, 0.65)",
                    cursor: "auto"
                  }}
                />
                <Form.Item name="brand_model_id" noStyle>
                  <Select
                    placeholder="Select Brand Model Code"
                    allowClear
                    showSearch
                    mode={"multiple"}
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={brandModelCodeOptions}
                  ></Select>
                </Form.Item>
              </Space.Compact>
            </Form.Item>{" "}
            <Form.Item>
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  disabled
                  value={"Zone"}
                  style={{ width: "200px", color: "rgba(0, 0, 0, 0.65)", cursor: "auto" }}
                />
                <Form.Item name="zone_id" noStyle>
                  <Select
                    mode="multiple"
                    placeholder="Select zone"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={zoneOptions}
                  ></Select>
                </Form.Item>
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={12}>
            <Form.Item
              label=""
              name="report_type"
              rules={[{ required: true, message: "Please select report" }]}
            >
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  disabled
                  value={"Report"}
                  style={{ width: "200px", color: "rgba(0, 0, 0, 0.65)", cursor: "auto" }}
                />
                <Form.Item name="report_type" noStyle>
                  <Select
                    placeholder="Select Report"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(inputValue, option: any) =>
                      option.label.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    options={
                      isClient
                        ? clientReportOptions
                        : isFinanceController
                          ? financeReportOptions
                          : filteredReportTypeOptions
                    }
                  ></Select>
                </Form.Item>
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col className="gutter-row" span={12}>
            <Button loading={loading} key="submit" htmlType="submit" type="primary">
              {loading ? "Downloading, please wait" : "Download"}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ReportDownloadModal;
